<script setup lang="ts">
import { computed, ref } from 'vue';
import { FeatureName, type Project, ProjectStatus } from '@factoryfixinc/ats-interfaces';
import { useRouter } from 'vue-router';

import MoreVerticalDots from '@/assets/svg/dots-vertical-20.svg?component';

import LightningPurpleIcon from '@/assets/svg/jobs/copilot-lightning-purple.svg';
import LightningBlackIcon from '@/assets/svg/jobs/copilot-lightning-black.svg';
import UsersPlus from '@/assets/svg/users-plus.svg';
import Duplicate from '@/assets/svg/sourcing/duplicate.svg';
import Edit from '@/assets/svg/edit-black.svg';
import Archive from '@/assets/svg/archive.svg';
import TrendyArrowUp from '@/assets/svg/sourcing/trendy-arrow-up.svg';
import DeleteRed from '@/assets/svg/sourcing/delete-red.svg';
import ClipboardDownload from '@/assets/svg/clipboard-download.svg';
import TrackingService from '@/core/shared/tracking/tracking.service';
import {
  ResumeUploadSource,
  TrackingActionName,
  TrackingCopilotSource,
} from '@/core/shared/tracking/tracking-actions';
import { ErrorService } from '@/core/shared/errors/error.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import ProjectService from '@/core/shared/project/project.service';
import { CustomerSuccessService } from '@/core/customer-success/customer-success.service';
import LightbulbOutlineSvg from '@/assets/svg/lightbulb-outline.svg';
import { DialogService } from '@/core/shared/dialog/dialog.service';
import { EmployerFeatureGatingService } from '@/core/shared/employer-feature-gating/employer-feature-gating.service';
import { UpgradeToGoldVariant } from '@/core/shared/dialog/dialog.types';

const TRACKING_SOURCE = TrackingCopilotSource.ACTION_MENU;

const props = defineProps<{
  modelValue: boolean;
  project: Project;
  isLoadingCopilot: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', payload: boolean): void;
  (e: 'toggleCopilot', checked: boolean, isActionMenu: boolean): void;
}>();

const router = useRouter();

const projectService = new ProjectService();
const customerSuccessService = new CustomerSuccessService();
const dialogService = new DialogService();
const featureGatingService = new EmployerFeatureGatingService();
const APPLICANT_FLOW_REQUEST_ERROR = 'Failed to request applicant flow analysis. Please try again.';
const isOpen = computed<boolean>(() => {
  return props.modelValue;
});
const projectStatus = computed(() => props.project.status);
const isCopilotActivated = computed(() => props.project.copilot);
const isArchived = computed(() => projectStatus.value === ProjectStatus.ARCHIVED);
const isLive = computed(() => projectStatus.value === ProjectStatus.LIVE);
const isLoading = ref<boolean>(false);
const hasAtsId = computed(() => Boolean(props.project.remoteJobId));
const isUploadCandidatesDisabled = computed(() => isDisabled.value || !isCopilotActivated.value);

function handleUpdateModelValue(value: boolean) {
  emit('update:modelValue', value);
}

async function handleCopyAtsId() {
  if (!props.project.remoteJobId) return;

  try {
    await navigator.clipboard.writeText(props.project.remoteJobId);
    SnackbarService.success('ATS ID copied to clipboard');
    handleUpdateModelValue(false);
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Failed to copy ATS ID to clipboard');
  }
}

async function handleArchiveProject() {
  if (isLoading.value) return;
  try {
    isLoading.value = true;
    const isCopilotActivated = props.project.copilot;
    await projectService.archiveProject(props.project.id, isCopilotActivated);
    await projectService.getProjectById(props.project.id);
    handleUpdateModelValue(false);
    TrackingService.trackAction(TrackingActionName.JOB_ARCHIVED, {
      source: TRACKING_SOURCE,
      copilot_status: copilotTrackingStatus.value,
      project_id: props.project.id,
      job_id: props.project.jobId,
    });
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Failed to archive job. Please try again later.');
  } finally {
    isLoading.value = false;
  }
}

async function handleUnarchiveProject() {
  if (isLoading.value) return;
  try {
    isLoading.value = true;
    await projectService.activateProject(props.project.id);
    await projectService.getProjectById(props.project.id);
    handleUpdateModelValue(false);
    TrackingService.trackAction(TrackingActionName.JOB_UNARCHIVED, {
      source: TRACKING_SOURCE,
      project_id: props.project.id,
      job_id: props.project.jobId,
    });
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Failed to unarchive job. Please try again later.');
  } finally {
    isLoading.value = false;
  }
}

async function handleDeleteProject() {
  if (isLoading.value) return;
  try {
    isLoading.value = true;
    await projectService.deleteProject(props.project.id);
    handleUpdateModelValue(false);
    TrackingService.trackAction(TrackingActionName.JOB_DELETED, {
      source: TRACKING_SOURCE,
      project_id: props.project.id,
      job_id: props.project.jobId,
    });
    await router.push('/conversations');
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Failed to delete job. Please try again later.');
  } finally {
    isLoading.value = false;
  }
}

async function handleDeactivateCopilot() {
  emit('toggleCopilot', isCopilotActivated.value, true);
  handleUpdateModelValue(false);
}

async function handelActivateCopilot() {
  // Activation will be handled in the ProjectCard component
  emit('toggleCopilot', isCopilotActivated.value, true);
}

async function handleEditJob() {
  if (isLoading.value) return;
  try {
    isLoading.value = true;
    TrackingService.trackAction(TrackingActionName.COPILOT_ENABLE_STARTED, {
      source: TrackingCopilotSource.EDIT_JOB,
      copilot_status: copilotTrackingStatus.value,
      project_id: props.project.id,
      job_id: props.project.jobId,
    });

    await router.push(`/jobs/${props.project.id}/copilot-activation`);
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Failed to edit job. Please try again later.');
  } finally {
    isLoading.value = false;
  }
}

async function cloneJob() {
  if (isLoading.value) return;
  try {
    isLoading.value = true;
    const duplicatedProject = await projectService.duplicateProject(props.project.id, {
      title: props.project.title,
      location: props.project.location,
    });
    handleUpdateModelValue(false);
    TrackingService.trackAction(TrackingActionName.COPILOT_ENABLE_STARTED, {
      source: TrackingCopilotSource.DUPLICATE_JOB,
      project_id: duplicatedProject.id,
      job_id: duplicatedProject.jobId,
    });

    await router.push(`/jobs/${duplicatedProject.id}/copilot-activation`);
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Failed to duplicate job. Please try again later.');
  } finally {
    isLoading.value = false;
  }
}

async function handleRequestApplicantFlowInsights() {
  if (isLoading.value) return;
  // Early gate check (outside try), mirrors left‑nav Labor Market Reports
  if (!featureGatingService.hasAccess(FeatureName.LABOR_MARKET_REPORTS)) {
    dialogService.openUpgradeToGoldModal(UpgradeToGoldVariant.LaborMarket);
    handleUpdateModelValue(false);
    return;
  }
  try {
    isLoading.value = true;
    const response = await customerSuccessService.submitApplicantFlowInsightsRequest(
      props.project.jobId,
      props.project.title,
      props.project.location,
    );
    if (response?.success) {
      dialogService.applicantFlowInsightsSuccessModalProps = {
        isOpen: true,
        referenceId: response.referenceId,
      };
    } else {
      SnackbarService.critical(response?.message || APPLICANT_FLOW_REQUEST_ERROR);
    }
    handleUpdateModelValue(false);
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical(APPLICANT_FLOW_REQUEST_ERROR);
  } finally {
    isLoading.value = false;
  }
}

const isDisabled = computed(() => isLoading.value || props.isLoadingCopilot);

const copilotTrackingStatus = computed(() => {
  return isCopilotActivated.value ? 'activated' : 'deactivated';
});
const showGoldPill = computed(
  () => !featureGatingService.hasAccess(FeatureName.LABOR_MARKET_REPORTS),
);
const menuWidthClass = computed(() => {
  // Sized to "Request app flow analysis" (with the Gold pill when shown) at the former 14px text,
  // plus the card's 12px padding, so no row label wraps or truncates; roomy at 12px.
  return showGoldPill.value ? 'w-[284px]' : 'w-[237px]';
});
function openUploadResumesModal() {
  dialogService.uploadResumesModalProps = {
    isOpen: true,
    jobId: props.project.jobId,
    activeOutreachEnabled: props.project.activeOutreachEnabled,
    source: ResumeUploadSource.PROJECT_ACTIONS_MENU,
  };
}
</script>
<template>
  <span>
    <v-menu
      :model-value="isOpen"
      location="bottom"
      transition="slide-y-transition"
      :close-on-content-click="false"
      @update:model-value="handleUpdateModelValue"
    >
      <template #activator="{ props }">
        <div v-bind="props" class="flex cursor-pointer items-center justify-center rounded-md">
          <MoreVerticalDots
            height="20"
            width="20"
            :class="isOpen ? 'text-tint-60' : 'text-tint-400'"
            class="mt-[-1px] transition-colors hover:!text-tint-60"
          />
        </div>
      </template>

      <div class="menu-card menu-card--sm mt-0.5" :class="menuWidthClass">
        <!-- Live -->
        <template v-if="isLive">
          <!-- Enable Copilot -->
          <button
            v-if="!isCopilotActivated"
            class="menu-item"
            :disabled="isDisabled"
            @click="handelActivateCopilot"
          >
            <img
              class="ml-0.5 mr-0.5"
              :src="LightningPurpleIcon"
              alt="Copilot"
              width="16"
              height="20"
            />
            <p class="mb-0 ml-2">Activate job</p>
          </button>

          <!-- Disable Copilot -->
          <button
            v-if="isCopilotActivated"
            class="menu-item"
            :disabled="isDisabled"
            @click="handleDeactivateCopilot"
          >
            <img
              class="ml-0.5 mr-0.5"
              :src="LightningBlackIcon"
              alt="Copilot"
              width="16"
              height="20"
            />
            <p class="mb-0 ml-2">Deactivate job</p>
          </button>

          <!-- Edit -->
          <button class="menu-item" :disabled="isDisabled" @click="handleEditJob">
            <img :src="Edit" alt="Edit" width="20" height="20" class="stroke-black" />
            <p class="mb-0 ml-2">Edit job</p>
          </button>

          <!-- Copy ATS ID -->
          <button v-if="hasAtsId" class="menu-item" :disabled="isDisabled" @click="handleCopyAtsId">
            <img :src="ClipboardDownload" alt="Copy ATS ID" width="20" height="20" />
            <p class="mb-0 ml-2">Copy ATS ID</p>
          </button>

          <!-- Duplicate -->
          <button class="menu-item" :disabled="isDisabled" @click="cloneJob">
            <img :src="Duplicate" alt="Duplicate" width="20" height="20" />
            <p class="mb-0 ml-2">Duplicate and edit job</p>
          </button>

          <!-- Upload Candidates -->
          <v-tooltip
            :disabled="!isUploadCandidatesDisabled"
            :open-on-focus="false"
            location="top"
            content-class="project-card-tooltip tooltip-bottom-arrow"
          >
            <template #activator="{ props }">
              <!-- A disabled button swallows pointer events and cannot be a tooltip activator. -->
              <div v-bind="props" class="w-full">
                <button
                  class="menu-item"
                  :disabled="isUploadCandidatesDisabled"
                  @click="openUploadResumesModal"
                >
                  <img :src="UsersPlus" alt="Upload candidates" width="20" height="20" />
                  <p class="mb-0 ml-2 whitespace-nowrap">Upload candidates</p>
                </button>
              </div>
            </template>
            <span class="text-xs">Activate this job to upload candidates</span>
          </v-tooltip>

          <!-- Request Applicant Flow Insights -->
          <button
            class="menu-item"
            :disabled="isDisabled"
            @click="handleRequestApplicantFlowInsights"
          >
            <img :src="LightbulbOutlineSvg" alt="Insights" width="20" height="20" />
            <p class="mb-0 ml-2 whitespace-nowrap">Request app flow analysis</p>
            <span
              v-if="showGoldPill"
              class="ml-2 rounded-full bg-gradient-to-r from-gold via-gold-light to-gold px-2 py-1 text-[10px] font-bold leading-[16px] text-shade-880"
            >
              Gold
            </span>
          </button>

          <!-- Archive -->
          <button class="menu-item" :disabled="isDisabled" @click="handleArchiveProject">
            <img :src="Archive" alt="Archive" width="20" height="20" />
            <p class="mb-0 ml-2">Archive</p>
          </button>
        </template>

        <!-- Archived -->
        <template v-if="isArchived">
          <!-- Unarchive -->
          <button class="menu-item" :disabled="isDisabled" @click="handleUnarchiveProject">
            <img :src="TrendyArrowUp" alt="Trendy Arrow Up" width="20" height="20" />
            <p class="mb-0 ml-2">Unarchive job</p>
          </button>

          <!-- Delete -->
          <button class="menu-item" :disabled="isDisabled" @click="handleDeleteProject">
            <img :src="DeleteRed" alt="Delete" width="20" height="20" />
            <p class="mb-0 ml-2">Delete job</p>
          </button>
        </template>
      </div>
    </v-menu>
  </span>
</template>
<style scoped lang="postcss">
/* This style indicates to the user that the action is loading and prevents further clicks */
:disabled:hover {
  @apply animate-linear-loading bg-gradient-to-r from-tint-80 via-tint-0 to-tint-80 bg-[length:200%_auto];
  @apply cursor-wait;
}
</style>
